'use client';

/**
 * useTabTracking
 *
 * 탭 전환 트래킹 훅
 *
 * @example
 * ```tsx
 * function LeaderboardTabs({ activeTab, onTabChange }) {
 *   const handleTabChange = useTabTracking('Leaderboard', onTabChange);
 *
 *   return (
 *     <Tabs value={activeTab} onChange={handleTabChange}>
 *       <Tab value="content">콘텐츠</Tab>
 *       <Tab value="creator">크리에이터</Tab>
 *     </Tabs>
 *   );
 * }
 * ```
 *
 * 자동 이벤트: `{prefix}: Tab Changed` with `{ tab: 'content' }`
 */
import { useCallback } from 'react';
import { Analytics } from '../tracker';

interface UseTabTrackingOptions {
  /** 이벤트와 함께 전송할 추가 속성 */
  properties?: Record<string, unknown>;
  /** 트래킹 활성화 여부 (기본값: true) */
  enabled?: boolean;
  /** 커스텀 이벤트 이름 (기본값: `{prefix}: Tab Changed`) */
  eventName?: string;
}

/**
 * 탭 전환 트래킹 훅
 *
 * @param prefix - 이벤트 prefix (예: 'Leaderboard')
 * @param onChange - 원래 탭 변경 핸들러
 * @param options - 옵션
 * @returns 트래킹이 포함된 탭 변경 핸들러
 */
export function useTabTracking<T = string>(
  prefix: string,
  onChange?: (tab: T) => void,
  options: UseTabTrackingOptions = {},
): (tab: T) => void {
  const { properties = {}, enabled = true, eventName } = options;

  const handleTabChange = useCallback(
    (tab: T) => {
      if (enabled) {
        const event = eventName || `${prefix}: Tab Changed`;
        Analytics.track(event, {
          tab: String(tab),
          ...properties,
        });
      }
      onChange?.(tab);
    },
    [prefix, onChange, properties, enabled, eventName],
  );

  return handleTabChange;
}
